import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ChevronLeft, Search, BookOpen, Zap, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AUTOMATION_CATEGORIES, TRIGGER_TYPES, ACTION_TYPES } from './automationConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutomationTemplateLibrary = ({ onSelect, onBack }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/automations/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return t.name.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query);
    }
    return true;
  });

  const getCategoryConfig = (category) => {
    return AUTOMATION_CATEGORIES.find(c => c.id === category) || { icon: '⚙️', label: category };
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Automation Templates
          </h1>
          <p className="text-sm text-muted-foreground">Pre-built automations to get you started quickly</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {AUTOMATION_CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <BookOpen className="h-12 w-12 mb-4 opacity-50" />
          <p>No templates found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => {
            const catConfig = getCategoryConfig(template.category);
            return (
              <Card
                key={template.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onSelect(template)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <Badge variant="outline">
                      {catConfig.icon} {catConfig.label}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm">
                    {template.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Triggers */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Triggers</p>
                      <div className="flex flex-wrap gap-1">
                        {template.triggers?.map((trigger, idx) => {
                          const config = TRIGGER_TYPES[trigger.type];
                          const Icon = config?.icon || Zap;
                          return (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              <Icon className="h-3 w-3 mr-1" />
                              {config?.label || trigger.type}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    {/* Actions Preview */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Actions</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {template.actions?.slice(0, 3).map((action, idx) => {
                          const config = ACTION_TYPES[action.type];
                          return (
                            <span key={idx} className="flex items-center gap-1">
                              {idx > 0 && <ArrowRight className="h-3 w-3" />}
                              {config?.label || action.type}
                            </span>
                          );
                        })}
                        {template.actions?.length > 3 && (
                          <span>+{template.actions.length - 3} more</span>
                        )}
                      </div>
                    </div>

                    <Button className="w-full mt-2" size="sm">
                      Use Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
